// src/features/analytics/components/AnalyticsRangeControl.tsx

import { useEffect, useState } from 'react';
import { Group, Select, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { DateTime } from 'luxon';
import { useNavigate } from '@tanstack/react-router';
import { SECONDS_PER_DAY } from '@/shared/utils/utcRange';
import { analyticsRouteApi } from '../routeApi';
import { useAnalyticsRange } from '../useAnalyticsRange';
import {
  ANALYTICS_PRESET_LABELS,
  ANALYTICS_RANGE_PRESETS,
  type AnalyticsRangePreset,
  type AnalyticsSearch,
} from '../searchParams';

function toUtcDateString(seconds: number): string {
  return DateTime.fromSeconds(seconds, { zone: 'utc' }).toFormat('yyyy-MM-dd');
}

function customValueFor(search: AnalyticsSearch): [string | null, string | null] {
  return search.preset === 'custom' && search.from !== undefined && search.to !== undefined
    ? [toUtcDateString(search.from), toUtcDateString(search.to - SECONDS_PER_DAY)]
    : [null, null];
}

/**
 * Preset selector + custom UTC-day range picker. Writes URL search params;
 * ranges are UTC calendar days, end-exclusive (`to` = midnight after the last
 * picked day). The picker floor follows the product window when configured.
 */
export function AnalyticsRangeControl() {
  const search = analyticsRouteApi.useSearch();
  const navigate = useNavigate();
  const { range, productWindowFloorSeconds } = useAnalyticsRange();

  const setSearch = (patch: Partial<AnalyticsSearch>) => {
    void navigate({
      to: '.',
      search: (prev) => ({ ...(prev as AnalyticsSearch), ...patch }),
    });
  };

  const handlePresetChange = (value: string | null) => {
    if (!value) return;
    const preset = value as AnalyticsRangePreset;
    if (preset === 'custom') {
      // Seed the custom range from the currently resolved range so the URL
      // stays valid (custom without from/to degrades to the default preset).
      setSearch({ preset, from: range.from, to: range.to });
    } else {
      setSearch({ preset, from: undefined, to: undefined });
    }
  };

  // Hold the in-progress selection locally so the first click survives. A fully
  // controlled range picker fed a partial `[start, null]` back as `[null, null]`
  // resets Mantine's pick and the range can never complete; commit to the URL
  // only when both ends are set, and resync when the resolved range changes.
  const [pending, setPending] = useState<[string | null, string | null]>(() =>
    customValueFor(search)
  );

  useEffect(() => {
    setPending(customValueFor(search));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.preset, search.from, search.to]);

  const handleCustomChange = (value: [string | null, string | null]) => {
    setPending(value);
    const [start, end] = value;
    if (!start || !end) return;
    const from = DateTime.fromFormat(start, 'yyyy-MM-dd', { zone: 'utc' });
    const lastDay = DateTime.fromFormat(end, 'yyyy-MM-dd', { zone: 'utc' });
    if (!from.isValid || !lastDay.isValid) return;
    setSearch({
      preset: 'custom',
      from: from.toSeconds(),
      to: lastDay.plus({ days: 1 }).toSeconds(),
    });
  };

  const todayUtc = toUtcDateString(Math.floor(Date.now() / 1000));

  return (
    <Group gap="sm" align="center">
      <Select
        data={ANALYTICS_RANGE_PRESETS.map((preset) => ({
          value: preset,
          label: ANALYTICS_PRESET_LABELS[preset],
        }))}
        value={search.preset}
        onChange={handlePresetChange}
        allowDeselect={false}
        w={150}
        size="sm"
        aria-label="Date range preset"
      />
      {search.preset === 'custom' && (
        <DatePickerInput
          type="range"
          allowSingleDateInRange
          value={pending}
          onChange={handleCustomChange}
          minDate={
            productWindowFloorSeconds !== null
              ? toUtcDateString(productWindowFloorSeconds)
              : undefined
          }
          maxDate={todayUtc}
          size="sm"
          w={260}
          aria-label="Custom date range (UTC)"
        />
      )}
      <Text size="xs" c="dimmed">
        Dates are UTC
      </Text>
    </Group>
  );
}
